import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import {
  Receipt,
  Pencil,
  Package,
  CreditCard,
  Lock,
  Plus,
  Minus,
  ArrowRight,
  ArrowLeft,
  BadgeDollarSign,
  Ban,
  RotateCcw,
  X,
  Printer,
  StickyNote,
  AlertTriangle,
  Tag,
  Zap,
  Copy,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import { getInvoiceHistoryAction } from "../../../redux/actions/customerAction";
import { getAllColorPinTagsAction } from "../../../redux/actions/pintagAction";

import CustomModal from "../../common/custom-modal/custom-modal";
import { formatDate } from "../../../util/common-functions";
import {
  IInvoiceHistoryEditDetail,
  InvoiceHistoryResponse,
} from "../../../typings/interfaces/responses/invoice-history-response";
import useTrayPrinter from "../../../hooks/useTrayPrinter";
import {
  loadPrintableByInvoice,
  loadPrintableFromSnapshot,
  describeSnapshotItems,
  buildTicketPrintRequest,
} from "../../../util/ticket-print";
import { useTranslation } from "../../../util/translation-provider";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

interface InvoiceHistoryModalProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  selectedId: number;
  customerId: string | number;
}

type StatusTone = "blue" | "green" | "red" | "gray" | "amber";

function getToneClasses(tone: StatusTone) {
  switch (tone) {
    case "green":
      return {
        dot: "bg-[#018752]",
        soft: "bg-[#018752]/10 text-[#018752] border-[#018752]/20",
        icon: "bg-[#018752]/10 text-[#018752]",
      };
    case "red":
      return {
        dot: "bg-[#930000]",
        soft: "bg-[#930000]/10 text-[#930000] border-[#930000]/20",
        icon: "bg-[#930000]/10 text-[#930000]",
      };
    case "amber":
      return {
        dot: "bg-[#C98900]",
        soft: "bg-[#C98900]/10 text-[#A66F00] border-[#C98900]/20",
        icon: "bg-[#C98900]/10 text-[#A66F00]",
      };
    case "gray":
      return {
        dot: "bg-[#9CA3AF]",
        soft: "bg-gray-100 text-[#515151] border-gray-200",
        icon: "bg-gray-100 text-[#515151]",
      };
    case "blue":
    default:
      return {
        dot: "bg-[#0079C2]",
        soft: "bg-[#0079C2]/10 text-[#0079C2] border-[#0079C2]/20",
        icon: "bg-[#0079C2]/10 text-[#0079C2]",
      };
  }
}

function getActionIcon(title: string) {
  // MANAGER_APPROVAL titles are "Manager Approval: <action>" (the suffix
  // varies per gated actionType - void, refund, edit, discount, etc), so
  // this is a prefix check rather than an exact-match switch case.
  if (title.startsWith("Manager Approval")) {
    return <ShieldCheck size={16} strokeWidth={2.2} />;
  }
  switch (title) {
    case "Order Created":
      return <Receipt size={16} strokeWidth={2.2} />;
    case "Order Edited":
      return <Pencil size={16} strokeWidth={2.2} />;
    case "Order Pick Up":
    case "Order Partially Pick Up":
      return <Package size={16} strokeWidth={2.2} />;
    case "Payment Received":
    case "Partial Payment Received":
    case "Marked as Pay Later":
    case "Added to Monthly Billing":
      return <CreditCard size={16} strokeWidth={2.2} />;
    case "Bill Voided":
      return <Ban size={16} strokeWidth={2.2} />;
    case "Refund Processed":
      return <RotateCcw size={16} strokeWidth={2.2} />;
    case "Note Added":
      return <StickyNote size={16} strokeWidth={2.2} />;
    case "Pin Tag Updated":
      return <Tag size={16} strokeWidth={2.2} />;
    case "Bill Closed":
    default:
      return <Lock size={16} strokeWidth={2.2} />;
  }
}

function getDotTone(type: string | null, title: string): StatusTone {
  switch (type) {
    case "PICKUP":
      return title === "Order Pick Up" ? "blue" : "amber";
    case "PAYMENT":
      return title === "Payment Received" ? "green" : "amber";
    case "VOIDED":
    case "REFUNDED":
      return "red";
    case "CLOSED":
    case "NOTE_ADDED":
      return "gray";
    case "PIN_TAG_UPDATED":
    case "MANAGER_APPROVAL":
      return "amber";
    case "CREATED":
    case "EDITED":
    default:
      return "blue";
  }
}

function DetailPill({ detail }: { detail: IInvoiceHistoryEditDetail }) {
  const baseClass = "flex items-center gap-2 rounded-xl border px-3 py-2 font-medium";
  const baseStyle = { fontSize: "var(--fs-2xs)" };

  const countText =
    detail.type === "added" || detail.type === "removed"
      ? typeof detail.count === "number"
        ? ` (${detail.count} pc${detail.count === 1 ? "" : "s"})`
        : ""
      : "";

  switch (detail.type) {
    case "added":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#018752]/20 bg-[#018752]/10 text-[#018752]`}>
          <Plus size={14} />
          <span>
            {detail.label}
            {countText}
          </span>
        </div>
      );
    case "removed":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#930000]/20 bg-[#930000]/10 text-[#930000]`}>
          <Minus size={14} />
          <span>
            {detail.label}
            {countText}
          </span>
        </div>
      );
    case "upcharged":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#C98900]/20 bg-[#C98900]/10 text-[#A66F00]`}>
          <BadgeDollarSign size={14} />
          <span>{detail.label}</span>
        </div>
      );
    case "price_changed":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#0079C2]/20 bg-[#0079C2]/10 text-[#0079C2]`}>
          <ArrowRight size={14} />
          <span>
            {detail.label} - {detail.garment}
          </span>
          <span className="ml-auto font-semibold text-[#515151]">
            {detail.from} → {detail.to}
          </span>
        </div>
      );
    case "garment_corrected":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#515151]/15 bg-[#515151]/5 text-[#515151]`}>
          <ArrowRight size={14} />
          <span>{detail.label}</span>
          <span className="ml-auto font-semibold text-[#515151]">
            {detail.fromGarment} → {detail.toGarment}
          </span>
        </div>
      );
    case "damage_noted":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#930000]/20 bg-[#930000]/10 text-[#930000]`}>
          <AlertTriangle size={14} />
          <span>
            {detail.label}
            {detail.garment ? ` - ${detail.garment}` : ""}
          </span>
        </div>
      );
    case "redo_flagged":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#C98900]/20 bg-[#C98900]/10 text-[#A66F00]`}>
          <RotateCcw size={14} />
          <span>
            {detail.label}
            {detail.garment ? ` - ${detail.garment}` : ""}
            {detail.to ? ` (${detail.to})` : ""}
          </span>
        </div>
      );
    case "discount_changed":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#0079C2]/20 bg-[#0079C2]/10 text-[#0079C2]`}>
          <Tag size={14} />
          <span>{detail.label}</span>
          <span className="ml-auto font-semibold text-[#515151]">
            {detail.from} → {detail.to}
          </span>
        </div>
      );
    case "rush_changed":
      return (
        <div style={baseStyle} className={`${baseClass} border-[#C98900]/20 bg-[#C98900]/10 text-[#A66F00]`}>
          <Zap size={14} />
          <span>{detail.label}</span>
        </div>
      );
    default:
      return null;
  }
}

// metadata.fromColor/toColor for a PIN_TAG_UPDATED event are actually the pin
// tag's short alias code (e.g. "M", "T2" -- see PintagColor::getAlias on the
// backend), not a real CSS color. Resolve the alias to its real hex color via
// the loaded pintag color list (state.pintag.getColorPintags); if the alias
// isn't found in that list, fall back to rendering the alias itself as a text
// chip so the change is still communicated instead of showing a blank swatch.
function PinTagColorChip({
  alias,
  colorMap,
}: {
  alias?: string | null;
  colorMap: Record<string, string>;
}) {
  if (!alias) {
    return (
      <span className="h-3.5 w-3.5 rounded-full border border-black/10 bg-transparent" />
    );
  }

  const resolvedColor = colorMap[alias.toLowerCase()];

  if (resolvedColor) {
    return (
      <span
        title={alias}
        aria-label={alias}
        className="h-3.5 w-3.5 shrink-0 rounded-full border border-black/10"
        style={{ backgroundColor: resolvedColor }}
      />
    );
  }

  return (
    <span
      title={alias}
      style={{ fontSize: "8px" }}
      className="inline-flex h-3.5 min-w-[0.9rem] shrink-0 items-center justify-center rounded-full border border-black/10 bg-[#515151]/10 px-1 font-semibold leading-none text-[#515151]"
    >
      {alias}
    </span>
  );
}

function StatusBadge({ label, tone }: { label: string; tone: StatusTone }) {
  const styles = getToneClasses(tone);
  const dotMap: Record<StatusTone, string> = {
    blue: "bg-[#0079C2]",
    green: "bg-[#018752]",
    red: "bg-[#930000]",
    gray: "bg-[#9CA3AF]",
    amber: "bg-[#C98900]",
  };

  return (
    <div
      style={{ fontSize: "var(--fs-2xs)" }}
      className={`inline-flex h-7 items-center gap-2 rounded-full border px-2.5 font-semibold ${styles.soft}`}
    >
      <span className={`h-2 w-2 rounded-full ${dotMap[tone]}`} />
      <span>{label}</span>
    </div>
  );
}

function EventCard({
  event,
  isLast,
  pinTagColorMap,
  onPickupCardClick,
}: {
  event: InvoiceHistoryResponse;
  isLast: boolean;
  pinTagColorMap: Record<string, string>;
  onPickupCardClick?: (eventId: number) => void;
}) {
  const tone = getToneClasses(getDotTone(event.type, event.title));
  const timeStr = new Date(event.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const metadata = event.metadata;
  const editDetails = metadata?.editDetails;
  const items = metadata?.items;
  const pickedUpItems = metadata?.pickedUpItems;
  const remainingItems = metadata?.remainingItems;
  const showStatusBadge = !!event.paymentStatusLabel;
  const hasPinTagChange =
    metadata?.fromColor !== undefined || metadata?.toColor !== undefined;
  // The approving manager's name must always be visible on a MANAGER_APPROVAL
  // event, per PV-FEAT-29 - prefer the structured metadata field (added
  // alongside the backend's InvoiceLifeCycle.MANAGER_APPROVAL emission) and
  // fall back to the flat description column, which the backend also sets
  // to the same name.
  const isManagerApproval = event.type === "MANAGER_APPROVAL";
  const managerApprovalName = metadata?.grantedByName ?? event.description;
  const isCreatedEvent = event.title === "Order Created";
  const originalBillSnapshot = metadata?.originalBillSnapshot;
  // Re-parses on every render rather than memoizing - this card only
  // re-renders when its own event/metadata changes (new array reference
  // from a fresh history fetch), so there's no stale-snapshot risk to guard
  // against, and the item count for one bill's snapshot is cheap to derive.
  const createdItems = isCreatedEvent && originalBillSnapshot
    ? describeSnapshotItems(originalBillSnapshot)
    : [];
  const hasStructuredContent =
    event.amount != null ||
    event.pieces != null ||
    event.checkNumber != null ||
    (editDetails && editDetails.length > 0) ||
    (items && items.length > 0) ||
    !!pickedUpItems ||
    hasPinTagChange ||
    isManagerApproval;

  return (
    <div className="grid grid-cols-[88px_24px_minmax(0,1fr)] gap-3">
      <div className="pt-1 text-right">
        <div style={{ fontSize: "var(--fs-base)" }} className="font-semibold text-[#515151]">
          {formatDate(event.createdAt)}
        </div>
        <div style={{ fontSize: "var(--fs-2xs)" }} className="mt-2 text-[#515151]/70">{timeStr}</div>
      </div>

      <div className="relative flex justify-center">
        {!isLast && (
          <div className="absolute bottom-[-22px] left-1/2 top-4 w-[2px] -translate-x-1/2 bg-[#0079C2]/25" />
        )}
        <div
          className={`relative z-10 mt-1 h-3.5 w-3.5 rounded-full border-2 border-white ${tone.dot}`}
        />
      </div>

      <div className="pb-4">
        <div className="rounded-2xl border border-[#0079C2] bg-white px-4 py-3">
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-xl ${tone.icon}`}
            >
              {getActionIcon(event.title)}
            </div>

            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-[#515151]">{event.title}</div>
                </div>
                <div style={{ fontSize: "var(--fs-2xs)" }} className="whitespace-nowrap text-[#515151]/70">
                  {event.cashierName ?? "Unknown"}
                  {event.cashierId ? ` · CSH${event.cashierId}` : ""}
                </div>
              </div>

              <div style={{ fontSize: "var(--fs-base)" }} className="mt-2 space-y-1.5 text-[#515151]">
                {event.amount != null ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[#515151]/60">Amount</span>
                    <span className="font-semibold">${event.amount.toFixed(2)}</span>
                  </div>
                ) : null}

                {event.checkNumber != null ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[#515151]/60">Check #</span>
                    <span className="font-semibold">{event.checkNumber}</span>
                  </div>
                ) : null}

                {event.pieces != null ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[#515151]/60">Pieces</span>
                    {event.previousPieces != null ? (
                      <span className="font-semibold">
                        {event.previousPieces} → {event.pieces}
                      </span>
                    ) : (
                      <span className="font-semibold">{event.pieces}</span>
                    )}
                  </div>
                ) : null}

                {event.balance != null && event.balance > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[#515151]/60">Balance</span>
                    <span className="font-semibold text-[#930000]">
                      ${event.balance.toFixed(2)}
                    </span>
                  </div>
                ) : null}

                {isManagerApproval ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[#515151]/60">Approved by</span>
                    <span className="font-semibold">
                      {managerApprovalName || "Manager"}
                    </span>
                  </div>
                ) : null}

                {pickedUpItems && remainingItems ? (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="View pickup item details"
                    onClick={() => onPickupCardClick?.(event.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPickupCardClick?.(event.id);
                      }
                    }}
                    style={{ fontSize: "var(--fs-2xs)" }}
                    className="grid cursor-pointer grid-cols-[1fr_1fr_auto] items-center gap-2 rounded-lg border border-transparent px-1 py-1 transition-colors hover:border-[#0079C2]/20 hover:bg-[#0079C2]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0079C2]/40"
                  >
                    <div className="rounded-lg bg-[#018752]/5 px-2 py-1">
                      <div className="font-medium text-[#018752]">
                        Picked: {pickedUpItems.length}
                      </div>
                    </div>
                    <div className="rounded-lg bg-[#930000]/5 px-2 py-1">
                      <div className="font-medium text-[#930000]">
                        Remaining: {remainingItems.length}
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      className="shrink-0 text-[#515151]/50"
                    />
                  </div>
                ) : null}

                {event.title === "Order Pick Up" && items && items.length > 0 ? (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="View pickup item details"
                    onClick={() => onPickupCardClick?.(event.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPickupCardClick?.(event.id);
                      }
                    }}
                    style={{ fontSize: "var(--fs-2xs)" }}
                    className="grid cursor-pointer grid-cols-[1fr_1fr_auto] items-center gap-2 rounded-lg border border-transparent px-1 py-1 transition-colors hover:border-[#0079C2]/20 hover:bg-[#0079C2]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0079C2]/40"
                  >
                    <div className="rounded-lg bg-[#018752]/5 px-2 py-1">
                      <div className="font-medium text-[#018752]">
                        Picked: {items.length}
                      </div>
                    </div>
                    <div className="rounded-lg bg-[#930000]/5 px-2 py-1">
                      <div className="font-medium text-[#930000]">
                        Remaining: 0
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      className="shrink-0 text-[#515151]/50"
                    />
                  </div>
                ) : null}

                {isCreatedEvent && createdItems.length > 0 ? (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="View original bill items"
                    onClick={() => onPickupCardClick?.(event.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPickupCardClick?.(event.id);
                      }
                    }}
                    style={{ fontSize: "var(--fs-2xs)" }}
                    className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-2 rounded-lg border border-transparent px-1 py-1 transition-colors hover:border-[#0079C2]/20 hover:bg-[#0079C2]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0079C2]/40"
                  >
                    <div className="rounded-lg bg-[#0079C2]/5 px-2 py-1">
                      <div className="font-medium text-[#0079C2]">
                        Items: {createdItems.length}
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      className="shrink-0 text-[#515151]/50"
                    />
                  </div>
                ) : null}

                {hasPinTagChange ? (
                  <div
                    style={{ fontSize: "var(--fs-2xs)" }}
                    className="flex flex-wrap items-center gap-4 rounded-lg bg-[#C98900]/5 px-2 py-1.5"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[#515151]/60">Color</span>
                      <PinTagColorChip
                        alias={metadata?.fromColor}
                        colorMap={pinTagColorMap}
                      />
                      <ArrowRight size={12} className="text-[#515151]/50" />
                      <PinTagColorChip
                        alias={metadata?.toColor}
                        colorMap={pinTagColorMap}
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[#515151]/60">Tags</span>
                      <span className="font-semibold text-[#515151]">
                        {metadata?.fromTags && metadata.fromTags.length > 0
                          ? metadata.fromTags.join(", ")
                          : "—"}
                      </span>
                      <ArrowRight size={12} className="text-[#515151]/50" />
                      <span className="font-semibold text-[#515151]">
                        {metadata?.toTags && metadata.toTags.length > 0
                          ? metadata.toTags.join(", ")
                          : "—"}
                      </span>
                    </div>
                  </div>
                ) : null}

                {editDetails && editDetails.length > 0 ? (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {editDetails.map((detail, index) => (
                      <DetailPill key={`${event.id}-${detail.type}-${index}`} detail={detail} />
                    ))}
                  </div>
                ) : null}

                {showStatusBadge ? (
                  <div className="flex justify-end pt-1">
                    <StatusBadge
                      label={event.paymentStatusLabel as string}
                      tone={(event.paymentStatusTone as StatusTone) ?? "blue"}
                    />
                  </div>
                ) : null}

                {!hasStructuredContent && event.description ? (
                  <div style={{ fontSize: "var(--fs-2xs)" }} className="text-[#515151]/70">{event.description}</div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Detail view for a single pickup event ("Order Partially Pick Up" OR the
// fully-picked-up "Order Pick Up"), shown in place of the timeline list when
// that event's card is tapped. Renders the union of pickedUpItems/
// remainingItems (item NAME strings, not ids -- duplicate names for identical
// garments are expected, so rows are keyed/rendered by array index rather
// than deduped). "Order Pick Up" events only ever populate `metadata.items`
// (never `metadata.pickedUpItems`), so the picked list falls back to
// `metadata.items` when `metadata.pickedUpItems` is absent; `remainingItems`
// is left as-is and naturally resolves to `[]` for that event type, since a
// bill can only reach "Order Pick Up" once every item has been picked.
function PickupDetailView({ event }: { event: InvoiceHistoryResponse }) {
  const pickedUpItems =
    event.metadata?.pickedUpItems ?? event.metadata?.items ?? [];
  const remainingItems = event.metadata?.remainingItems ?? [];
  const timeStr = new Date(event.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div>
      <div className="mb-4">
        <div style={{ fontSize: "var(--fs-md)" }} className="font-bold text-[#515151]">
          {event.title}
        </div>
        <div style={{ fontSize: "var(--fs-2xs)" }} className="mt-1 text-[#515151]/70">
          {formatDate(event.createdAt)} · {timeStr}
        </div>
      </div>

      <div className="space-y-2">
        {pickedUpItems.map((name, index) => (
          <div
            key={`picked-${index}`}
            style={{ fontSize: "var(--fs-base)" }}
            className="flex items-center justify-between rounded-lg bg-[#018752]/5 px-3 py-2"
          >
            <span className="text-[#515151]">{name}</span>
            <span
              style={{ fontSize: "var(--fs-2xs)" }}
              className="rounded-full bg-[#018752]/10 px-2.5 py-1 font-semibold text-[#018752]"
            >
              Picked
            </span>
          </div>
        ))}

        {remainingItems.map((name, index) => (
          <div
            key={`remaining-${index}`}
            style={{ fontSize: "var(--fs-base)" }}
            className="flex items-center justify-between rounded-lg bg-[#930000]/5 px-3 py-2"
          >
            <span className="text-[#515151]">{name}</span>
            <span
              style={{ fontSize: "var(--fs-2xs)" }}
              className="rounded-full bg-[#930000]/10 px-2.5 py-1 font-semibold text-[#930000]"
            >
              Remaining
            </span>
          </div>
        ))}

        {pickedUpItems.length === 0 && remainingItems.length === 0 ? (
          <div style={{ fontSize: "var(--fs-base)" }} className="pt-8 text-center text-[#515151]/60">
            No items recorded for this pickup event.
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Detail view for an "Order Created" event's card - the bill's original item
// list, derived from the frozen originalBillSnapshot (see
// describeSnapshotItems), with no picked/remaining split since nothing has
// been picked up yet at the moment a bill is created.
function CreatedItemsDetailView({
  event,
  onPrintOriginalClick,
  isPrintingOriginal,
}: {
  event: InvoiceHistoryResponse;
  onPrintOriginalClick?: (event: InvoiceHistoryResponse) => void;
  isPrintingOriginal?: boolean;
}) {
  const items = event.metadata?.originalBillSnapshot
    ? describeSnapshotItems(event.metadata.originalBillSnapshot)
    : [];
  const timeStr = new Date(event.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div style={{ fontSize: "var(--fs-md)" }} className="font-bold text-[#515151]">
            {event.title}
          </div>
          <div style={{ fontSize: "var(--fs-2xs)" }} className="mt-1 text-[#515151]/70">
            {formatDate(event.createdAt)} · {timeStr}
          </div>
        </div>
        <button
          type="button"
          aria-label="Print original bill"
          onClick={() => onPrintOriginalClick?.(event)}
          disabled={isPrintingOriginal}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[#0079C2]/30 text-[#0079C2] hover:bg-[#0079C2]/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPrintingOriginal ? (
            <CircularProgress size={16} sx={{ color: "#0079C2" }} />
          ) : (
            <Printer size={18} strokeWidth={2.2} />
          )}
        </button>
      </div>

      <div className="space-y-2">
        {items.map((name, index) => (
          <div
            key={`created-item-${index}`}
            style={{ fontSize: "var(--fs-base)" }}
            className="flex items-center rounded-lg bg-[#0079C2]/5 px-3 py-2"
          >
            <span className="text-[#515151]">{name}</span>
          </div>
        ))}

        {items.length === 0 ? (
          <div style={{ fontSize: "var(--fs-base)" }} className="pt-8 text-center text-[#515151]/60">
            No items recorded for this bill.
          </div>
        ) : null}
      </div>
    </div>
  );
}

const InvoiceHistoryModal: React.FC<InvoiceHistoryModalProps> = ({
  open,
  onClose,
  billId,
  selectedId,
  customerId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const translation = useTranslation();
  const snackbar = useSnackbarContext();
  const { printTicket, isPrinting } = useTrayPrinter();
  const [printingTicket, setPrintingTicket] = useState(false);
  // Tracks which "Order Created" event is currently printing its original
  // bill - keyed by event id, same reasoning as expandedPickupEventId below
  // (a bill's timeline could theoretically show more than one CREATED-type
  // row, e.g. after a duplicate-bill flow).
  const [printingOriginalId, setPrintingOriginalId] = useState<number | null>(
    null
  );
  // Tracks which pickup event's ("Order Partially Pick Up" OR "Order Pick
  // Up") detail view is currently shown in place of the timeline (null =
  // timeline view). Keyed by event id rather than a boolean because a single
  // bill's timeline can contain more than one pickup event of either type,
  // and each card must expand independently.
  const [expandedPickupEventId, setExpandedPickupEventId] = useState<
    number | null
  >(null);

  const invoiceHistory = useSelector(
    (state: RootState) => state.customer.invoiceHistory
  );

  const invoiceHistoryLoading = useSelector(
    (state: RootState) => state.customer.invoiceHistoryLoading
  );

  const colorPintags = useSelector(
    (state: RootState) => state.pintag.getColorPintags
  );

  // Depends on `open`, not just `selectedId` - this component stays mounted
  // (the parent only clears `selectedInvoice` to null, it never unmounts on
  // close) for as long as the customer history table is open, so reopening
  // the SAME invoice's timeline right after a payment or pickup on it would
  // otherwise reuse the stale fetch from the first time it was opened
  // instead of picking up the event that just happened.
  useEffect(() => {
    if (billId && open) {
      dispatch(getInvoiceHistoryAction(Number(selectedId)));
    }
  }, [selectedId, open, dispatch]);

  // Reset the pickup detail view whenever the modal closes or a different
  // bill's timeline is selected, so reopening never leaves a stale event's
  // detail view showing.
  useEffect(() => {
    setExpandedPickupEventId(null);
  }, [selectedId, open]);

  // Needed to resolve PIN_TAG_UPDATED events' fromColor/toColor -- those are
  // pin tag alias codes, not CSS colors -- to their real hex color for display.
  useEffect(() => {
    dispatch(getAllColorPinTagsAction());
  }, [dispatch]);

  const pinTagColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    (colorPintags?.body ?? []).forEach((pintag) => {
      if (pintag.alias) {
        map[pintag.alias.toLowerCase()] = pintag.color;
      }
    });
    return map;
  }, [colorPintags]);

  const events = invoiceHistory ?? [];

  const expandedPickupEvent =
    expandedPickupEventId != null
      ? events.find((event) => event.id === expandedPickupEventId) ?? null
      : null;

  const handlePrintTicket = async () => {
    if (isPrinting || printingTicket) return;

    setPrintingTicket(true);
    try {
      const payload = await loadPrintableByInvoice(
        dispatch,
        translation,
        String(selectedId)
      );
      if (!payload) {
        snackbar.showSnackbar("Could not load ticket for printing.", "error");
        return;
      }

      const printData = buildTicketPrintRequest(payload, "receipt");
      const ok = await printTicket(printData);
      if (!ok) {
        snackbar.showSnackbar(
          "Could not reach the printer. Make sure Presto Tray is running on this computer.",
          "error"
        );
      }
    } finally {
      setPrintingTicket(false);
    }
  };

  const handlePrintOriginal = async (event: InvoiceHistoryResponse) => {
    if (printingOriginalId != null) return;

    setPrintingOriginalId(event.id);
    try {
      const payload = await loadPrintableFromSnapshot(
        dispatch,
        translation,
        event,
        billId,
        customerId
      );
      if (!payload) {
        snackbar.showSnackbar(
          "Could not load the original bill for printing.",
          "error"
        );
        return;
      }

      const printData = buildTicketPrintRequest(payload, "receipt");
      const ok = await printTicket(printData);
      if (!ok) {
        snackbar.showSnackbar(
          "Could not reach the printer. Make sure Presto Tray is running on this computer.",
          "error"
        );
      }
    } finally {
      setPrintingOriginalId(null);
    }
  };

  const handleDuplicateBill = () => {
    onClose();
    navigate(`/pos/${customerId}?duplicateFrom=${selectedId}`);
  };

  return (
    <CustomModal
      title=""
      openModal={open}
      onClose={onClose}
      mode="popup"
      hideCloseButton
      width="51.7857142857rem"
      height="70.8571428571rem"
      sx={{
        padding: 0,
        overflow: "hidden",
        borderRadius: "0.43rem",
        backgroundColor: "#F4F4F4",
      }}
    >
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        {expandedPickupEvent ? (
          <button
            type="button"
            aria-label="Back to timeline"
            onClick={() => setExpandedPickupEventId(null)}
            className="absolute left-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#515151]/80 text-[#515151] hover:bg-white/60"
          >
            <ArrowLeft size={22} />
          </button>
        ) : null}

        <button
          type="button"
          aria-label="Close timeline"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#515151]/80 text-[#515151] hover:bg-white/60"
        >
          <X size={22} />
        </button>

        {expandedPickupEvent ? null : (
          <div className="flex items-center gap-3 px-12 pt-8">
            <span
              style={{ fontSize: "var(--fs-md)" }}
              className="rounded-2xl bg-[#0079C2] px-4 py-2 font-bold text-white"
            >
              {`IN${billId}`}
            </span>
            <button
              type="button"
              aria-label="Print ticket"
              onClick={handlePrintTicket}
              disabled={isPrinting || printingTicket}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#0079C2]/30 text-[#0079C2] hover:bg-[#0079C2]/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {printingTicket ? (
                <CircularProgress size={16} sx={{ color: "#0079C2" }} />
              ) : (
                <Printer size={18} strokeWidth={2.2} />
              )}
            </button>
            <button
              type="button"
              aria-label="Duplicate bill"
              onClick={handleDuplicateBill}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#0079C2]/30 text-[#0079C2] hover:bg-[#0079C2]/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Copy size={18} strokeWidth={2.2} />
            </button>
          </div>
        )}

        <div
          className={`px-12 pb-8 ${expandedPickupEvent ? "pt-20" : "pt-4"}`}
        >
          <div className="relative h-[700px] overflow-y-auto pr-3">
            {expandedPickupEvent ? (
              expandedPickupEvent.title === "Order Created" ? (
                <CreatedItemsDetailView
                  event={expandedPickupEvent}
                  onPrintOriginalClick={handlePrintOriginal}
                  isPrintingOriginal={printingOriginalId === expandedPickupEvent.id}
                />
              ) : (
                <PickupDetailView event={expandedPickupEvent} />
              )
            ) : events.length > 0 ? (
              events.map((event, index) => (
                <EventCard
                  key={event.id}
                  event={event}
                  isLast={index === events.length - 1}
                  pinTagColorMap={pinTagColorMap}
                  onPickupCardClick={setExpandedPickupEventId}
                />
              ))
            ) : invoiceHistoryLoading ? (
              <div className="flex justify-center pt-24">
                <CircularProgress />
              </div>
            ) : (
              <div style={{ fontSize: "var(--fs-base)" }} className="pt-8 text-center text-[#515151]/60">
                No history available
              </div>
            )}
          </div>
        </div>
      </Box>
    </CustomModal>
  );
};

export default InvoiceHistoryModal;
